import type { FarmState, FarmEvent, ProtocolSection } from '../domain/model';
import { createDomain } from '../domain/farm';
import { preserveCompletedTasks } from '../domain/history';
import { todayISO, addDays, fmtDate, validDate } from '../domain/dates';
import type { FormRequest, FormValues } from './types';

// A retry of the same open form is one application; opening a new form is a new realization.
const procedureApplicationIds=new WeakMap<FormRequest,string>();

export function executeForm(request:FormRequest, values:FormValues, state:FarmState, showToast:(message:string)=>void):boolean {
  const draft=structuredClone(state);
  preserveCompletedTasks(draft);
  if(!executeInternal(request,values,draft,showToast))return false;
  Object.assign(state,draft);
  return true;
}

function executeInternal(request:FormRequest, values:FormValues, state:FarmState, showToast:(message:string)=>void):boolean {
 const args=request.args||[];
 const value=(id:string)=>values[id]||'';
 const selected=(id:string):string[]=>{try{const parsed:unknown=JSON.parse(value(id)||'[]');return Array.isArray(parsed)&&parsed.every(item=>typeof item==='string')?[...new Set(parsed)]:[]}catch{return []}};
 const uid=(prefix:string)=>prefix+'_'+crypto.randomUUID();
 const fail=(message='El registro ya no está disponible.')=>{showToast(message);return false;};
 const textArg=(index:number)=>typeof args[index]==='string'?args[index]:'';
 const CERDA_ESTADOS_INACTIVOS=['muerta','vendida','retirada'];
 const { getCerda,getVerraco,getLote,getCamada,getEspacio,getEspacioCerda,getCamadaDeParto,partosDeCerda,verracoIdsDeMonta,getActiveMonta,syncCerdaReproState,marcarParaImprimir,ponerCerdaEnObservacion,fechaFinLactancia,normalizarOrdenEspaciosCerdas,origenDeCamada,cerdaEnObservacion,destinatariosTarea,eventSubject,eventAction }=createDomain(state);
 switch(request.kind){
case 'reprogramarEvento': {
const candidate=args[0] as Partial<FarmEvent>|undefined;
const evento=candidate?.doneKey?createDomain(state).generateAllEvents().find(event=>event.doneKey===candidate.doneKey):undefined;
if(!evento)return fail('La actividad ya no está disponible.');


    const modo=value('fModoFecha');
    if(!['original','fecha','sinFecha'].includes(modo))return fail('Selecciona una programación válida.');
    if(modo==='original') delete state.eventOverrides[evento.doneKey];
    else{
      const fecha=modo==='sinFecha'?'':value('fFechaEvento');
      if(modo==='fecha'&&!validDate(fecha)){ showToast('Elige una fecha válida'); return false; }
      const titulo=evento.manualId?'':value('fTituloEvento').trim();
      if(!evento.manualId&&!titulo){ showToast('Escribe la actividad'); return false; }
      state.eventOverrides[evento.doneKey]={date:fecha,...(evento.manualId?{}:{title:titulo,detail:value('fDetalleEvento').trim()})};
    }
     showToast('Programación actualizada'); return true;

}
case 'formTareaManual': {
const id = textArg(0);
const tarea=id?state.tareasManual.find(t=>t.id===id):null;
  if(id && !tarea)return fail('No se encontró la tarea');

    const fecha=value('fFechaTarea');
    const titulo=value('fTituloTarea').trim();
    if(!fecha || !titulo){ showToast('Indica el día y el nombre de la tarea'); return false; }
    const destinatarioTipo=value('fTipoDestinatarioTarea');
    const destinatarioId=destinatarioTipo==='general'?'':value('fDestinatarioTarea');
    if(destinatarioTipo!=='general'&&!destinatarioId){ showToast('Selecciona a quién le corresponde la tarea'); return false; }
    const destinatarioNombre=destinatarioTipo==='general'?'':
      destinatariosTarea(destinatarioTipo).find(d=>d.id===destinatarioId)?.nombre||tarea?.destinatarioNombre||'';
    const datos={fecha,titulo,categoria:value('fCategoriaTarea'),detalle:value('fDetalleTarea').trim(),destinatarioTipo,destinatarioId,destinatarioNombre};
    if(tarea) Object.assign(tarea,datos); else state.tareasManual.push({id:uid('tm'),...datos});
     showToast(tarea?'Tarea actualizada':'Tarea registrada'); return true;

}
case 'eliminarTareaManual': {
const id = args[0] as string;
const tarea=state.tareasManual.find(t=>t.id===id); if(!tarea) return false;

    state.tareasManual=state.tareasManual.filter(t=>t.id!==id);
    state.tareasHechas=state.tareasHechas.filter(key=>key!==`manual|${id}`);
     showToast('Tarea eliminada'); return true;

}
case 'formNuevaCerda': {



    const arete=value('fArete').trim();
    if(!arete){ showToast('Falta el número de arete'); return false; }
    if(getCerda(arete)){ showToast('Ya existe una cerda con ese arete'); return false; }
    const nuevaCerda={
      arete, raza:value('fRaza').trim(), origen:value('fOrigen'),
      lugarCompra:value('fLugarCompra').trim(), fechaNacimiento:value('fFNac'),
      fechaEntrada:value('fFEntrada'), primeriza:value('fPrimeriza')==='si',
      estado:value('fEstadoInicial'),fechaInicioReemplazo:value('fFEntrada'),
      partosPrevios:0, numPartos:0, enObservacion:value('fObservacion')==='si',
      motivoObservacion:value('fMotivoObservacion').trim(),observaciones:value('fObs').trim()
    };
    marcarParaImprimir(nuevaCerda,'Registro de nueva cerda');
    state.cerdas.push(nuevaCerda);
     showToast('Cerda registrada'); return true;

}
case 'formNuevoVerraco': {



    const nombre=value('fNombre').trim();
    if(!nombre){ showToast('Falta el nombre'); return false; }
    const nuevoVerraco={id:uid('v'),nombre,raza:value('fRaza').trim(),fechaNacimiento:value('fFNac'),fechaEntrada:value('fFEntrada'),estado:value('fEstado'),observaciones:value('fObs').trim()};
    marcarParaImprimir(nuevoVerraco,'Registro de nuevo verraco');
    state.verracos.push(nuevoVerraco);
     showToast('Verraco registrado'); return true;

}
case 'formMonta': {
const arete = args[0] as string;
const montaId = textArg(1);
const cerda=getCerda(arete);
  const monta=montaId?state.montas.find(m=>m.id===montaId):null;
  if(!cerda||(montaId&&(!monta||monta.arete!==arete)))return fail();
  const verracosAnteriores=verracoIdsDeMonta(monta);
  const ultimoParto=partosDeCerda(arete)[0];
  const fechaSugerida=!monta&&ultimoParto&&todayISO()<=ultimoParto.fecha?addDays(ultimoParto.fecha,1):todayISO();
  const seleccionados=verracoIdsDeMonta(monta);
  const cantidadVerracos=seleccionados.length>=2?'2':seleccionados.length?'1':'0';

    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha de la monta'); return false; }
    const cantidad=+value('fCantidadVerracos');
    const verracoIds=[];
    if(cantidad>=1) verracoIds.push(value('fVerraco1'));
    if(cantidad===2) verracoIds.push(value('fVerraco2'));
    if(verracoIds.some(id=>!id)){ showToast(`Selecciona ${cantidad===2?'los dos verracos':'el verraco'}`); return false; }
    if(new Set(verracoIds).size!==verracoIds.length){ showToast('Selecciona dos verracos diferentes'); return false; }
    const datos={
      arete, fecha, tipoServicio:value('fTipo'),
      verracoIds, verracoId:verracoIds[0]||'', verracoExterno:value('fVerracoExt').trim(),
      proveedor:value('fProveedor').trim()
    };
    if(verracoIds.some(id=>!getVerraco(id))||![0,1,2].includes(cantidad))return fail('Selecciona verracos registrados.');
    const guardada=monta||{id:uid('m'),gestacionConfirmada:false,...datos};
    if(monta) Object.assign(monta,datos); else state.montas.push(guardada);
    syncCerdaReproState(arete);
    marcarParaImprimir(cerda,`Monta del ${fmtDate(fecha)}`);
    [...new Set([...verracosAnteriores,...verracoIds])].forEach(id=>marcarParaImprimir(getVerraco(id),`Monta con cerda ${arete} del ${fmtDate(fecha)}`));
    const esActiva=getActiveMonta(arete)?.id===guardada.id;
     showToast(monta?'Monta actualizada':esActiva?'Monta registrada; ya puedes confirmar la gestación':'Monta histórica guardada; existe un parto o repetición posterior'); return true;

}
case 'formCelo': {
const arete = args[0] as string;
const celoId = textArg(1);
const celo=celoId?state.celos.find(c=>c.id===celoId):null;
if(!getCerda(arete)||(celoId&&(!celo||celo.arete!==arete)))return fail();

    const fecha=value('fFechaCelo');
    if(!fecha){ showToast('Selecciona la fecha del celo'); return false; }
    const datos={arete,fecha,observaciones:value('fObservacionesCelo').trim()};
    if(celo){
      if(celo.fecha!==fecha) state.tareasHechas=state.tareasHechas.filter(key=>!key.startsWith(`celo|${celo.id}|`));
      Object.assign(celo,datos);
    }else state.celos.push({id:uid('cl'),...datos});
    syncCerdaReproState(arete);
    marcarParaImprimir(getCerda(arete),`Celo del ${fmtDate(fecha)}`);
     showToast(celo?'Celo actualizado; calendario recalculado':'Celo registrado; días 18 al 21 agendados'); return true;

}
case 'formRepeticion': {
const arete = args[0] as string;
const repeticionId = textArg(1);
const repeticion=repeticionId?state.repeticiones.find(r=>r.id===repeticionId):null;
if(!getCerda(arete)||(repeticionId&&(!repeticion||repeticion.arete!==arete)))return fail();

    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha de repetición'); return false; }
    const datos={arete,fecha,infeccion:value('fInfeccion')==='si'};
    if(repeticion) Object.assign(repeticion,datos); else state.repeticiones.push({id:uid('rp'),...datos});
    if(datos.infeccion) ponerCerdaEnObservacion(arete,'Repetición con infección');
    syncCerdaReproState(arete);
    marcarParaImprimir(getCerda(arete),`Repetición del ${fmtDate(fecha)}`);
     showToast(repeticion?'Repetición actualizada':'Repetición registrada; calendario actualizado'); return true;

}
case 'formGestacion': {
const arete = args[0] as string;
const montaId = args[1] as string;
const monta=state.montas.find(m=>m.id===montaId&&m.arete===arete); if(!monta||!getCerda(arete)) return fail();

    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha de confirmación'); return false; }
    if(fecha<monta.fecha){ showToast('La confirmación no puede ser anterior a la monta'); return false; }
    monta.gestacionConfirmada=true; monta.fechaConfirmacion=fecha; monta.metodoConfirmacion=value('fMetodo'); monta.observacionGestacion=value('fObs').trim();
    syncCerdaReproState(arete); marcarParaImprimir(getCerda(arete),`Gestación confirmada el ${fmtDate(fecha)}`);  showToast('Gestación confirmada'); return true;

}
case 'formParto': {
const arete = args[0] as string;
const partoId = textArg(1);
const cerda=getCerda(arete);
  const parto=partoId?state.partos.find(p=>p.id===partoId):null;
  if(!cerda||(partoId&&(!parto||parto.arete!==arete)))return fail();

    const hem=+value('fHem')||0, mac=+value('fMac')||0;
    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha del parto'); return false; }
    const peso=+value('fPeso')||0;
    const pezonesCampo=value('fPezones');
    const pezonesFuncionales=pezonesCampo===''?null:Number(pezonesCampo);
    if(pezonesFuncionales!==null&&(!Number.isInteger(pezonesFuncionales)||pezonesFuncionales<0)){ showToast('Indica una cantidad válida de pezones funcionales'); return false; }
    const intervencion=value('fIntervencion');
    if(!parto&&intervencion===''){ showToast('Indica si el parto necesitó intervención manual'); return false; }
    const datos={arete,fecha,hembras:hem,machos:mac,nacidosVivos:hem+mac,nacidosMuertos:+value('fMuertos')||0,momias:+value('fMomias')||0,pesoCamada:peso,pezonesFuncionales,intervencionManual:intervencion===''?null:intervencion==='si',observaciones:value('fObs').trim()};
    if(datos.intervencionManual) ponerCerdaEnObservacion(arete,'Parto distócico con intervención manual');
    else if(datos.nacidosMuertos>0) ponerCerdaEnObservacion(arete,'Nacidos muertos en el parto');
    if(parto){
      const camada=getCamadaDeParto(parto.id);
      if(camada){
        const diferencia=(hem+mac)-(camada.cantidadInicial||0);
        camada.fechaNacimiento=fecha; camada.cantidadInicial=hem+mac; camada.cantidadActual=Math.max(0,(camada.cantidadActual||0)+diferencia);
        camada.pesoPromedio=(hem+mac)>0?peso/(hem+mac):0;
        if(camada.loteId){ const lote=getLote(camada.loteId); if(lote) lote.cantidadActual=Math.max(0,(lote.cantidadActual||0)+diferencia); }
      }
      Object.assign(parto,datos);
      syncCerdaReproState(arete);
      marcarParaImprimir(cerda,`Parto actualizado del ${fmtDate(fecha)}`);
       showToast('Parto y camada actualizados'); return true;
    }
    const nuevoPartoId=uid('parto');
    state.partos.push({id:nuevoPartoId,...datos});
    let secuencia=(cerda.partosPrevios||0)+partosDeCerda(arete).length;
    let camadaId=`P-${String(secuencia).padStart(2,'0')}-${arete}`;
    while(getCamada(camadaId)){ secuencia++; camadaId=`P-${String(secuencia).padStart(2,'0')}-${arete}`; }
    state.camadas.push({id:camadaId,partoId:nuevoPartoId,arete,fechaNacimiento:fecha,cantidadInicial:hem+mac,cantidadActual:hem+mac,pesoPromedio:(hem+mac)>0?peso/(hem+mac):0,muertes:[],loteId:null});
    syncCerdaReproState(arete); cerda.primeriza=false;
    marcarParaImprimir(cerda,`Parto del ${fmtDate(fecha)}`);
     showToast(`Parto registrado — camada ${camadaId} creada`); return true;

}
case 'formRacionLactancia': {
const arete = args[0] as string;
const partoId = args[1] as string;
const racionId = textArg(2);
const parto=state.partos.find(p=>p.id===partoId&&p.arete===arete);
  if(!parto) return false;
  const racion=racionId?state.racionesLactancia.find(r=>r.id===racionId):null;
  if(racionId&&(!racion||racion.partoId!==partoId||racion.arete!==arete))return fail();
  const primerDia=addDays(parto.fecha,1);
  const finPeriodo=fechaFinLactancia(parto);

    const fechaDesde=value('fFechaRacion');
    const campoCerda=value('fLibrasCerda');
    const campoLechones=value('fLibrasLechones');
    const librasCerda=Number(campoCerda),librasLechones=Number(campoLechones);
    if(!fechaDesde||fechaDesde<primerDia||fechaDesde>todayISO()||(finPeriodo&&fechaDesde>finPeriodo)){
      showToast(`Elige una fecha de lactancia desde ${fmtDate(primerDia)} hasta hoy`); return false;
    }
    if(campoCerda===''||campoLechones===''||![librasCerda,librasLechones].every(n=>Number.isFinite(n)&&n>=0)){
      showToast('Indica libras diarias válidas para cerda y lechones'); return false;
    }
    if(state.racionesLactancia.some(item=>item.partoId===partoId&&item.fechaDesde===fechaDesde&&item.id!==racionId)){
      showToast('Ya hay una ración desde esa fecha; edítala'); return false;
    }
    const datos={arete,partoId,fechaDesde,librasCerda,librasLechones};
    if(racion) Object.assign(racion,datos); else state.racionesLactancia.push({id:uid('rac'),...datos});
     showToast('Ración diaria guardada'); return true;

}
case 'eliminarRacionLactancia': {
const id = args[0] as string;
const racion=state.racionesLactancia.find(item=>item.id===id);
  if(!racion) return false;

    state.racionesLactancia=state.racionesLactancia.filter(item=>item.id!==id);
     showToast('Ración eliminada'); return true;

}
case 'eliminarMonta': {
const id = args[0] as string;
const monta=state.montas.find(m=>m.id===id); if(!monta) return false;
  const verracos=verracoIdsDeMonta(monta);

    state.montas=state.montas.filter(m=>m.id!==id);
    state.tareasHechas=state.tareasHechas.filter(k=>!k.includes(`|${id}|`));
    syncCerdaReproState(monta.arete);
    marcarParaImprimir(getCerda(monta.arete),'Monta eliminada');
    verracos.forEach(verracoId=>marcarParaImprimir(getVerraco(verracoId),'Monta eliminada'));
     showToast('Monta eliminada'); return true;

}
case 'eliminarRepeticion': {
const id = args[0] as string;
const repeticion=state.repeticiones.find(r=>r.id===id); if(!repeticion) return false;

    state.repeticiones=state.repeticiones.filter(r=>r.id!==id);
    syncCerdaReproState(repeticion.arete); marcarParaImprimir(getCerda(repeticion.arete),'Repetición eliminada');  showToast('Repetición eliminada'); return true;

}
case 'eliminarCelo': {
const id = args[0] as string;
const celo=state.celos.find(c=>c.id===id); if(!celo) return false;

    state.celos=state.celos.filter(c=>c.id!==id);
    state.tareasHechas=state.tareasHechas.filter(key=>!key.startsWith(`celo|${id}|`));
    syncCerdaReproState(celo.arete);
    marcarParaImprimir(getCerda(celo.arete),'Celo eliminado');
     showToast('Celo eliminado'); return true;

}
case 'eliminarParto': {
const id = args[0] as string;
const parto=state.partos.find(p=>p.id===id); if(!parto) return false;
  const camada=getCamadaDeParto(id);
  if(camada?.loteId){ showToast(`No se puede eliminar: la camada pertenece al lote ${camada.loteId}. Puedes editar el parto.`); return false; }
  if(camada&&state.transferenciasLechones.some(t=>t.origenCamadaId===camada.id||t.destinoCamadaId===camada.id)){ showToast('No se puede eliminar: la camada tiene adopciones cruzadas. Reviértelas primero.'); return false; }

    state.partos=state.partos.filter(p=>p.id!==id);
    if(camada){
      state.camadas=state.camadas.filter(c=>c.id!==camada.id);
      state.pesajes=state.pesajes.filter(p=>!(p.tipoRef==='camada'&&p.ref===camada.id));
      state.mortalidad=state.mortalidad.filter(m=>m.camadaId!==camada.id);
      state.tareasHechas=state.tareasHechas.filter(k=>!k.startsWith(`lechon|${camada.id}|`));
    }
    state.racionesLactancia=state.racionesLactancia.filter(racion=>racion.partoId!==id);
    state.tareasHechas=state.tareasHechas.filter(k=>!k.startsWith(`posparto|${id}|`));
    syncCerdaReproState(parto.arete); marcarParaImprimir(getCerda(parto.arete),'Parto eliminado');  showToast('Parto eliminado'); return true;

}
case 'editarCerda': {
const arete = args[0] as string;
const c=getCerda(arete);
if(!c)return fail();

    const partosPrevios=+value('fPartosPrevios');
    if(!Number.isInteger(partosPrevios)||partosPrevios<0){ showToast('Indica una cantidad válida de partos previos'); return false; }
    c.raza=value('fRaza').trim();
    c.origen=value('fOrigen');
    c.lugarCompra=value('fLugarCompra').trim();
    c.fechaNacimiento=value('fFNac');
    c.fechaEntrada=value('fFEntrada');
    c.fechaInicioReemplazo=value('fFReemplazo');
    const situacion=value('fEstado');
    c.estado=situacion==='activa'?'vacía':situacion;
    c.partosPrevios=partosPrevios;
    c.numPartos=partosPrevios+partosDeCerda(arete).length;
    c.primeriza=c.numPartos>0?false:value('fPrimeriza')==='si';
    c.enObservacion=value('fObservacion')==='si';
    c.motivoObservacion=value('fMotivoObservacion').trim();
    c.observaciones=value('fObs').trim();
    syncCerdaReproState(arete);
    marcarParaImprimir(c,'Datos de la cerda actualizados');
     showToast('Cerda actualizada'); return true;

}
case 'eliminarCerda': {
const arete = args[0] as string;
if(!getCerda(arete))return fail();

    state.cerdas=state.cerdas.filter(c=>c.arete!==arete);
    state.espaciosCerdas.forEach(espacio=>{ if(espacio.cerdaArete===arete) espacio.cerdaArete=null; });
     showToast('Cerda eliminada');

    return true;

}
case 'formNuevoLote': {

    const origenManual=value('fOrigenLote')==='manual';
    const ids=origenManual?[]:selected('fCamadas');
    if(!origenManual&&!ids.length){ showToast('Selecciona al menos una camada'); return false; }
    const cantidadManual=+value('fCantidadManual');
    if(origenManual&&(!Number.isInteger(cantidadManual)||cantidadManual<1)){ showToast('Indica una cantidad inicial válida'); return false; }
    const etapa=value('fEtapa');
    const espacioId=etapa==='paridera'?'':value('fEspacio');
    const espacio=getEspacio(espacioId);
    if(espacioId&&(!espacio||espacio.loteId||espacio.tipo!==etapa))return fail('El espacio ya no está disponible para esta etapa.');
    const camadas=ids.map(id=>getCamada(id));
    if(camadas.some(c=>!c||c.loteId||!(c.cantidadActual>0)))return fail('Una camada ya no está disponible o no tiene lechones.');
    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha de creación'); return false; }
    const edadTexto=value('fEdad');
    const diaEdadAlCrear=Number(edadTexto);
    if(edadTexto===''||!Number.isInteger(diaEdadAlCrear)||diaEdadAlCrear<0){ showToast('Indica una edad de 0 días o más'); return false; }
    const fechaNacimientoReferencia=addDays(fecha,-diaEdadAlCrear);
    const peso=+value('fPeso')||0;
    if(camadas.some(c=>c&&fecha<c.fechaNacimiento))return fail('La formación no puede ser anterior al nacimiento de las camadas.');
    const cantidad=origenManual?cantidadManual:camadas.reduce((sum,c)=>sum+(c?.cantidadActual||0),0);
    let loteId:string;
    do { state.counters.lote=(state.counters.lote||0)+1;loteId='L'+String(state.counters.lote).padStart(2,'0'); } while(getLote(loteId));
    const nuevoLote={id:loteId,camadaIds:ids,fechaCreacion:fecha,diaEdadAlCrear,fechaNacimientoReferencia,estado:'activo',espacioActual:espacioId||null,cantidadActual:cantidad,
      origenManual:origenManual?{sinRegistroParto:true,detalle:value('fOrigenManual').trim()}:null,
      incorporaciones:[],historial:[{espacioId:espacioId||null,tipo:etapa,fechaEntrada:fecha,fechaSalida:null,pesoEntrada:peso,pesoSalida:null,cantidad}]};
    marcarParaImprimir(nuevoLote,'Registro de nuevo lote');
    state.lotes.push(nuevoLote);
    camadas.forEach(c=>{if(c)c.loteId=loteId;});
    if(espacio) espacio.loteId=loteId;
     showToast(`Lote ${loteId} creado`); return true;

}
case 'formAgregarCamadaLote': {
const loteId = args[0] as string;
const lote=getLote(loteId);
  const disponibles=state.camadas.filter(c=>!c.loteId && c.cantidadActual>0);
  if(!lote||lote.estado!=='activo') return false;
  if(!disponibles.length){ showToast('No hay camadas libres con lechones para agregar'); return false; }

    const camada=getCamada(value('fCamada'));
    const fecha=value('fFecha');
    if(!camada||camada.loteId||!(camada.cantidadActual>0)||!fecha){ showToast('Selecciona una camada libre con lechones y una fecha'); return false; }
    if(fecha<lote.fechaCreacion){ showToast('La fecha no puede ser anterior a la formación del lote'); return false; }
    if(fecha<camada.fechaNacimiento)return fail('La incorporación no puede ser anterior al nacimiento de la camada.');
    if(fecha>todayISO()){ showToast('La incorporación debe registrarse en una fecha actual o anterior'); return false; }
    const cantidad=+camada.cantidadActual||0;
    if(cantidad<1){ showToast('Esta camada no tiene lechones para incorporar'); return false; }
    lote.camadaIds.push(camada.id);
    lote.incorporaciones.push({id:uid('inc'),camadaId:camada.id,fecha,cantidad});
    lote.cantidadActual=(+lote.cantidadActual||0)+cantidad;
    camada.loteId=loteId;
    marcarParaImprimir(lote,`Camada ${camada.id} incorporada`);
     showToast(`Camada ${camada.id} agregada al lote`); return true;

}
case 'formMovimientoLote': {
const loteId = args[0] as string;
    const lote=getLote(loteId);
    if(!lote||lote.estado!=='activo')return fail('El lote no está activo.');
    const fecha=value('fFecha');
    if(!fecha){ showToast('Selecciona la fecha del movimiento'); return false; }
    const destino=value('fDestino');
    const nuevoEsp=destino==='salida'?'':value('fEspacioDestino');
    if(destino!=='salida'&&!nuevoEsp){ showToast(`Selecciona un espacio de ${destino} disponible`); return false; }
    const espacioDestino=getEspacio(nuevoEsp);
    if(destino!=='salida'&&(!espacioDestino||espacioDestino.tipo!==destino||espacioDestino.loteId))return fail('El espacio destino ya no está disponible.');
    const ult=lote.historial[lote.historial.length-1];
    if(!ult||fecha<ult.fechaEntrada)return fail('El movimiento no puede ser anterior a la última entrada.');
    ult.fechaSalida=fecha; ult.pesoSalida=+value('fPesoSalida')||0;
    const espacioAnterior=getEspacio(lote.espacioActual);
    if(espacioAnterior?.loteId===loteId)espacioAnterior.loteId=null;
    if(destino==='salida'){
      lote.estado='finalizado'; lote.espacioActual=null; lote.fechaSalidaFinca=fecha;
    }else{
      lote.espacioActual=nuevoEsp;
      if(espacioDestino) espacioDestino.loteId=loteId;
      lote.historial.push({espacioId:nuevoEsp,tipo:destino,fechaEntrada:fecha,fechaSalida:null,pesoEntrada:+value('fPesoEntrada')||0,pesoSalida:null,cantidad:lote.cantidadActual});
    }
    marcarParaImprimir(lote,`Movimiento del lote el ${fmtDate(fecha)}`);
     showToast('Movimiento registrado'); return true;

}
case 'formAlimentacion': {
const loteId = args[0] as string;
    if(!getLote(loteId))return fail();
    if(value('fHasta')<value('fDesde'))return fail('La fecha hasta no puede ser anterior a desde.');
    state.alimentacion.push({id:uid('al'),loteId,desde:value('fDesde'),hasta:value('fHasta'),libras:+value('fLibras')||0,faseId:value('fFase')});
    marcarParaImprimir(getLote(loteId),'Alimentación del lote registrada');
     showToast('Alimentación registrada'); return true;

}
case 'formMortalidad': {
const loteId = args[0] as string;
const lote=getLote(loteId);
if(!lote)return fail();

    const cantidad=Math.max(1,+value('fCantidad')||1);
    if(cantidad>lote.cantidadActual){ showToast('La cantidad supera los animales actuales del lote'); return false; }
    lote.cantidadActual=Math.max(0,(lote.cantidadActual||0)-cantidad);
    state.mortalidad.push({id:uid('mo'),loteId,cantidad,fecha:value('fFecha'),etapa:value('fEtapa'),causa:value('fCausa').trim()});
    marcarParaImprimir(lote,'Mortalidad del lote registrada');
     showToast('Mortalidad registrada'); return true;

}
case 'formPesaje': {
const loteId = args[0] as string;
    if(!getLote(loteId))return fail();
    state.pesajes.push({id:uid('pe'),ref:loteId,tipoRef:'lote',evento:value('fEvento').trim(),fecha:value('fFecha'),pesoTotal:+value('fPeso')||0,cantidad:+value('fCantidad')||0});
    marcarParaImprimir(getLote(loteId),'Pesaje del lote registrado');
     showToast('Pesaje registrado'); return true;

}
case 'formMortalidadCamada': {
const camadaId = args[0] as string;
const camada=getCamada(camadaId); if(!camada) return false;
if(camada.loteId)return fail('La camada ya pertenece a un lote. Registra la mortalidad desde su ficha de lote.');

    const cantidad=Math.max(1,+value('fCantidad')||1);
    if(cantidad>camada.cantidadActual){ showToast('La cantidad supera los lechones actuales'); return false; }
    camada.cantidadActual-=cantidad;
    state.mortalidad.push({id:uid('mo'),camadaId,cantidad,fecha:value('fFecha'),etapa:'maternidad',causa:value('fCausa').trim()});
    ponerCerdaEnObservacion(camada.arete,'Mortalidad de lechones en lactancia');
     showToast('Mortalidad de camada registrada'); return true;

}
case 'formPesajeCamada': {
const camadaId = args[0] as string;
const camada=getCamada(camadaId); if(!camada) return false;
if(camada.loteId)return fail('La camada ya pertenece a un lote. Registra el pesaje desde su ficha de lote.');

    const peso=+value('fPeso')||0, cantidad=+value('fCantidad')||0;
    if(!peso||!cantidad){ showToast('Completa peso y cantidad'); return false; }
    state.pesajes.push({id:uid('pe'),ref:camadaId,tipoRef:'camada',evento:value('fEvento').trim(),fecha:value('fFecha'),pesoTotal:peso,cantidad});
    camada.pesoPromedio=peso/cantidad;
     showToast('Pesaje de camada registrado'); return true;

}
case 'formTransferenciaLechones': {
const camadaOrigenId = args[0] as string;
const origen=getCamada(camadaOrigenId); if(!origen) return false;
  if(origen.loteId){ showToast(`La camada ya pertenece al lote ${origen.loteId}`); return false; }
  if(origen.cantidadActual<=0){ showToast('Esta camada no tiene lechones disponibles para donar'); return false; }
  const destinos=state.camadas.filter(c=>c.id!==origen.id&&!c.loteId).sort((a,b)=>a.fechaNacimiento<b.fechaNacimiento?1:-1);
  if(!destinos.length){ showToast('Necesitas otra camada libre para recibir los lechones'); return false; }
  const origenDatos=origenDeCamada(origen);

    const destino=getCamada(value('fDestino'));
    const cantidad=Math.floor(+value('fCantidad')||0);
    const fecha=value('fFecha');
    if(!destino||destino.id===origen.id||destino.loteId||origen.loteId){ showToast('Una de las camadas ya no está disponible'); return false; }
    if(cantidad<1||cantidad>origen.cantidadActual){ showToast(`Puedes donar entre 1 y ${origen.cantidadActual} lechones`); return false; }
    if(!fecha||fecha<origen.fechaNacimiento||fecha<destino.fechaNacimiento){ showToast('La fecha debe ser posterior al nacimiento de ambas camadas'); return false; }
    origen.cantidadActual-=cantidad; destino.cantidadActual=(destino.cantidadActual||0)+cantidad;
    state.transferenciasLechones.push({id:uid('tr'),fecha,origenCamadaId:origen.id,destinoCamadaId:destino.id,cantidad,observaciones:value('fObs').trim()});
     showToast(`${cantidad} lechón${cantidad===1?'':'es'} trasladado${cantidad===1?'':'s'} a ${destino.id}`); return true;

}
case 'eliminarTransferenciaLechones': {
const id = args[0] as string;
const t=state.transferenciasLechones.find(x=>x.id===id); if(!t) return false;
  const origen=getCamada(t.origenCamadaId), destino=getCamada(t.destinoCamadaId);
  if(!origen||!destino){ showToast('No se encontraron ambas camadas'); return false; }
  if(origen.loteId||destino.loteId){ showToast('No se puede revertir: una de las camadas ya pertenece a un lote'); return false; }
  if(destino.cantidadActual<t.cantidad){ showToast('No se puede revertir: la receptora ya no tiene esa cantidad disponible'); return false; }

    destino.cantidadActual-=t.cantidad; origen.cantidadActual+=t.cantidad;
    state.transferenciasLechones=state.transferenciasLechones.filter(x=>x.id!==id);
     showToast('Donación revertida'); return true;

}
case 'editarVerraco': {
const id = args[0] as string;
const v=getVerraco(id);
if(!v)return fail();

    v.nombre=value('fNombre').trim();
    v.raza=value('fRaza').trim();
    v.fechaNacimiento=value('fFNac');
    v.fechaEntrada=value('fFEntrada');
    v.estado=value('fEstado');
    v.observaciones=value('fObs').trim();
    marcarParaImprimir(v,'Datos del verraco actualizados');
     showToast('Verraco actualizado'); return true;

}
case 'eliminarVerraco': {
const id = args[0] as string;
if(!getVerraco(id))return fail();

    state.verracos=state.verracos.filter(v=>v.id!==id);
     showToast('Verraco eliminado');  return true;

}
case 'editarLote': {
const id = args[0] as string;
const l=getLote(id);
if(!l)return fail();

    const fecha=value('fFecha');
    const edadTexto=value('fEdad');
    const diaEdadAlCrear=Number(edadTexto);
    if(!fecha){ showToast('Selecciona la fecha de formación'); return false; }
    if(edadTexto===''||!Number.isInteger(diaEdadAlCrear)||diaEdadAlCrear<0){ showToast('Indica una edad de 0 días o más'); return false; }
    const fechaAnterior=l.fechaCreacion;
    if(l.historial?.[0]?.fechaEntrada===fechaAnterior) l.historial[0].fechaEntrada=fecha;
    l.fechaCreacion=fecha;
    l.diaEdadAlCrear=diaEdadAlCrear;
    l.fechaNacimientoReferencia=addDays(fecha,-diaEdadAlCrear);
    l.estado=value('fEstado');
    l.cantidadActual=+value('fCantidad')||0;
    marcarParaImprimir(l,'Datos del lote actualizados');
     showToast('Lote actualizado'); return true;

}
case 'eliminarLote': {
const id = args[0] as string;


    const lote=getLote(id);
    if(!lote)return fail();
    const espacio=getEspacio(lote.espacioActual);
    if(espacio?.loteId===id) espacio.loteId=null;
    lote.camadaIds.forEach(cid=>{ const cam=getCamada(cid); if(cam?.loteId===id) cam.loteId=null; });
    state.lotes=state.lotes.filter(l=>l.id!==id);
     showToast('Lote eliminado');  return true;

}
case 'formNuevoEspacio': {
const espacioId = textArg(0);
const espacio=espacioId?getEspacio(espacioId):null;
  const ocupado=!!espacio?.loteId;

    const id=value('fId').trim();
    if(!id){ showToast('Falta el identificador'); return false; }
    if(getEspacio(id)&&getEspacio(id)!==espacio){ showToast('Ese espacio ya existe'); return false; }
    if(espacio){
      const anterior=espacio.id;
      if(ocupado&&value('fTipo')!==espacio.tipo)return fail('No se puede cambiar el tipo de un espacio ocupado.');
      espacio.id=id; espacio.tipo=value('fTipo');
      if(anterior!==id){
        state.lotes.forEach(lote=>{
          if(lote.espacioActual===anterior) lote.espacioActual=id;
          (lote.historial||[]).forEach(h=>{ if(h.espacioId===anterior) h.espacioId=id; });
        });
      }
       showToast('Espacio actualizado'); return true;
    }
    state.espacios.push({id,tipo:value('fTipo'),loteId:null});
     showToast('Espacio registrado'); return true;

}
case 'eliminarEspacio': {
const id = args[0] as string;
const espacio=getEspacio(id); if(!espacio) return false;
  if(espacio.loteId){ showToast(`No se puede eliminar: está ocupado por el lote ${espacio.loteId}`); return false; }

    state.espacios=state.espacios.filter(e=>e.id!==id);
     showToast('Espacio eliminado'); return true;

}
case 'formEspacioCerda': {
const tipoInicial = textArg(0)||'gestacion';
const espacioId = textArg(1);
const espacio=espacioId?getEspacioCerda(espacioId):null;
  const tipo=espacio?.tipo||tipoInicial;
  const siguienteOrden=state.espaciosCerdas.filter(e=>e.tipo===tipo).reduce((max,e)=>Math.max(max,+e.orden||0),0)+1;

    const nombre=value('fNombreEspacioCerda').trim();
    const nuevoTipo=value('fTipoEspacioCerda');
    const orden=+value('fOrdenEspacioCerda');
    if(!nombre){ showToast('Escribe el identificador del espacio'); return false; }
    if(!Number.isInteger(orden)||orden<1){ showToast('Indica una posición válida'); return false; }
    const repetido=state.espaciosCerdas.some(e=>e.id!==espacioId&&e.nombre.toLowerCase()===nombre.toLowerCase());
    if(repetido){ showToast('Ya existe un espacio con ese identificador'); return false; }
    if(espacio){
      const tipoAnterior=espacio.tipo;
      espacio.nombre=nombre; espacio.tipo=nuevoTipo;
      const fila=state.espaciosCerdas.filter(e=>e.tipo===nuevoTipo&&e.id!==espacio.id).sort((a,b)=>(+a.orden||0)-(+b.orden||0));
      fila.splice(Math.min(orden-1,fila.length),0,espacio);
      fila.forEach((e,index)=>{ e.orden=index+1; });
      if(tipoAnterior!==nuevoTipo) normalizarOrdenEspaciosCerdas();
       showToast('Espacio actualizado'); return true;
    }
    const nuevo={id:uid('espcerda'),nombre,tipo:nuevoTipo,orden,cerdaArete:null};
    const fila=state.espaciosCerdas.filter(e=>e.tipo===nuevoTipo).sort((a,b)=>(+a.orden||0)-(+b.orden||0));
    fila.splice(Math.min(orden-1,fila.length),0,nuevo);
    fila.forEach((e,index)=>{ e.orden=index+1; });
    state.espaciosCerdas.push(nuevo);
     showToast('Espacio creado'); return true;

}
case 'formAsignarCerdaEspacio': {
const espacioId = args[0] as string;
const espacio=getEspacioCerda(espacioId); if(!espacio) return false;
  const activas=state.cerdas.filter(c=>!CERDA_ESTADOS_INACTIVOS.includes(c.estado)).sort((a,b)=>a.arete.localeCompare(b.arete,undefined,{numeric:true}));

    const arete=value('fCerdaEspacio');
    if(arete&&!activas.some(c=>c.arete===arete))return fail('La cerda ya no está disponible.');
    if(arete) state.espaciosCerdas.forEach(e=>{ if(e.cerdaArete===arete) e.cerdaArete=null; });
    espacio.cerdaArete=arete||null;
     showToast(arete?`Cerda ${arete} asignada`:'Espacio liberado'); return true;

}
case 'eliminarEspacioCerda': {
const espacioId = args[0] as string;
const espacio=getEspacioCerda(espacioId); if(!espacio) return false;

    state.espaciosCerdas=state.espaciosCerdas.filter(e=>e.id!==espacioId);
    normalizarOrdenEspaciosCerdas();
     showToast('Espacio eliminado'); return true;

}
case 'editarProtocolo': {
const seccion = args[0] as ProtocolSection;
const id = args[1] as string;
const lista=state.protocolos[seccion];
if(!lista)return fail('Sección de protocolo inválida.');
  const p=id?lista.find(x=>x.id===id):null;
if(id&&!p)return fail();

    const dia=+value('fDia');
    const nombre=value('fNombre').trim();
    if(!Number.isInteger(dia)||dia<1||!nombre){ showToast('Completa un día entero desde 1 y el nombre'); return false; }
    const detalle=value('fDetalle').trim();
    const aplicaA= seccion==='reproduccion'? value('fAplica') : undefined;
    if(p){ p.dia=dia; p.nombre=nombre; p.detalle=detalle; if(aplicaA) p.aplicaA=aplicaA; }
    else{ lista.push({id:uid('pr'),dia,nombre,detalle,aplicaA,activo:true}); }
     showToast('Guardado'); return true;

}
case 'eliminarProtocolo': {
  const seccion=textArg(0) as ProtocolSection,id=textArg(1);
  const lista=state.protocolos[seccion];
  if(!lista?.some(p=>p.id===id))return fail();
  state.protocolos[seccion]=lista.filter(p=>p.id!==id);
  showToast('Paso de protocolo eliminado');return true;
}
case 'editarProcedimiento': {
const id = textArg(0);
const procedimiento=id?state.procedimientos.find(p=>p.id===id):null;
  if(id&&!procedimiento) return false;

    const titulo=value('fTitulo').trim();
    if(!titulo){ showToast('Falta el encabezado'); return false; }
    const datos={titulo,acciones:value('fAcciones').trim()};
    if(procedimiento) Object.assign(procedimiento,datos);
    else state.procedimientos.push({id:uid('proc'),...datos});
     showToast(procedimiento?'Procedimiento actualizado':'Procedimiento guardado'); return true;

}
case 'eliminarProcedimiento': {
const id = args[0] as string;
if(!state.procedimientos.some(p=>p.id===id)) return false;

    state.procedimientos=state.procedimientos.filter(p=>p.id!==id);
     showToast('Procedimiento eliminado'); return true;

}
case 'formAplicacionProcedimiento': {
    const procedimiento=state.procedimientos.find(p=>p.id===value('fProcedimientoAplicacion'));
    if(!procedimiento)return fail('Selecciona un procedimiento registrado.');
    const fecha=value('fFechaAplicacion');
    if(!validDate(fecha)||fecha>todayISO())return fail('Indica una fecha de realización válida que no sea futura.');
    const type=value('fTipoDestinatarioAplicacion'),id=value('fDestinatarioAplicacion');
    if(!['cerda','verraco','lote'].includes(type))return fail('Selecciona un tipo de destinatario válido.');
    const recipient=destinatariosTarea(type).find(recipient=>recipient.id===id);
    if(!recipient)return fail('El destinatario ya no está disponible.');
    let applicationId=procedureApplicationIds.get(request);
    if(!applicationId){applicationId=uid('aproc');procedureApplicationIds.set(request,applicationId);}
    if(state.aplicacionesProcedimientos?.some(application=>application.id===applicationId)){
      showToast('La aplicación ya estaba registrada.');return true;
    }
    const ref={type,id};
    const area=createDomain(state).eventArea({date:fecha,tipo:'procedimiento',prioridad:1,label:procedimiento.titulo,doneKey:`procedimiento|${applicationId}`,ref});
    (state.aplicacionesProcedimientos??=[]).push({id:applicationId,procedimientoId:procedimiento.id,fecha,titulo:procedimiento.titulo,acciones:procedimiento.acciones,detalle:value('fDetalleAplicacion').trim(),ref,subject:recipient.nombre,area});
    const registro=type==='cerda'?getCerda(id):type==='verraco'?getVerraco(id):getLote(id);
    marcarParaImprimir(registro,`Procedimiento: ${procedimiento.titulo} del ${fmtDate(fecha)}`);
    showToast('Aplicación registrada en el historial.');return true;
}
 default:showToast('Acción no disponible');return false;
 }
 return false;
}
